/**
 * Student-answer extraction. Serves the first extraction, submissions added later, and retries of
 * failed submissions — previously three separate copies of the same loop with slightly different
 * progress bookkeeping.
 *
 * SPLIT INTO OPENING A RUN AND DOING ONE UNIT, because a submission is now one job. This step used to be
 * the only one that owned all three parts of a run — opening it, doing every unit, and closing it — and
 * that stopped being true the moment `runStudentAnswersStep(examId, { submissionIds: [one] })` became the
 * per-job call: every job re-opened and re-closed the exam-wide progress record, so after the FIRST of ten
 * submissions landed the record read COMPLETED at 1/10. `anySetupStepOngoing` and the re-grade gate both
 * believed it.
 *
 * So the three parts now sit where they sit for every other step:
 *  - `openAnswerExtractionRun` — the site that knows the round (a split confirmation, a retry) opens it,
 *    exactly as `openMasterSolutionGenerationRun` does for its own run;
 *  - `extractAnswersForSubmission` — one job, one submission, pull-on-start/push-on-finish, no status writes;
 *  - `syncBatchProgress('answer')` — closes and announces when the batch drains.
 *
 * THERE IS NO WHOLE-ROUND FUNCTION LEFT. `runStudentAnswersStep` was the fourth caller — it opened a run,
 * extracted every submission inline and closed the run itself — and it survived the move to jobs for two
 * callers that had none: the dead `pipeline/run.ts` path, and the retry tool's single-submission case. The
 * first is deleted; the second dispatches a job and waits for it like every other synchronous caller
 * (`dispatchAnswerExtractionAndWait`). Keeping it would have kept a second way to run this step that skipped
 * the lease, the retry, the LLM gate and the batch's closing — the last of which it papered over by writing
 * COMPLETED itself, which is wrong whenever a batch for the same run is still live.
 */

import { logger } from '@adornis/base/logging.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import {
  addErrorSubmissionByExam,
  markUnitFinished,
  markUnitStarted,
  setStatusByExam,
  setTotalByExam,
  StepState,
} from '../../extraction-progress.js';
import { Submission } from '../../submission.js';
import { processSubmissionById } from '../agents/extraction-modules/process-submission.js';
import { createCostTracker, loadStepInput } from './step-utils.js';

/**
 * Opens an answer-extraction run: marks the record IN_PROGRESS and recounts its total.
 *
 * There is no round OBJECT to open beside it: the step is running while the exam has a live `answerExtraction`
 * row and finished when it does not. `dispatchAnswerExtraction` is the only caller, and progress is all this owns.
 *
 * The bar an exam shows is "of all submissions ever uploaded", not "of this round" — so `total` is COUNTED
 * from the collection rather than accumulated. Accumulating it (`+= this round`) is what made a re-upload
 * add its submissions to a total that already included every earlier one; a count cannot drift because it
 * never depends on what the number was before.
 *
 * There is no retry branch any more, and that is the point of the set. The bookkeeping a retry used to need
 * — give back the finished count, clear the error marks — is what `markUnitStarted` does per unit, so this
 * function no longer has to be told whether the round is new work or a repeat.
 *
 * It does NOT close anything. The wave's completion does that, once the round's last row is terminal.
 */
export async function openAnswerExtractionRun(examId: string): Promise<void> {
  // AT DISPATCH, not at the first claim, and it is worth knowing the cost: these rows are enqueued when the split is
  // confirmed and gated on the structure being confirmed, which is a TEACHER — so between those two the bar reads
  // IN_PROGRESS while nothing is running. Moving it to the claim would be more truthful and is a deliberate
  // non-change: `anySetupStepOngoing` is a pure function of exam state, so it can only see the status, and a step
  // whose rows are merely queued would stop holding the re-grade settle window and the announcement gate.
  await setStatusByExam(examId, 'answer', StepState.IN_PROGRESS, 'progress.extractingAnswers');

  const submissionCollection = await getCollection(Submission);
  await setTotalByExam(examId, 'answer', await submissionCollection.countDocuments({ examId } as any));
}

/**
 * Extracts ONE submission's answers — the unit of an answer-extraction run, and what a job runs.
 *
 * Deliberately writes no status. A per-submission call has no opinion about whether the RUN is finished,
 * and the version of this that did was reporting the whole step complete after its first submission.
 *
 * PULL ON START, PUSH ON FINISH. `markUnitStarted` takes this submission out of the done set and clears its
 * error mark; `markUnitFinished` puts it back on success. Redoing a submission that already succeeded
 * therefore walks the bar back by exactly one and forward again when it lands, and a job that dies halfway
 * leaves it outstanding — neither needs a caller to know it was a repeat. A FAILED submission is left out of
 * the set on purpose: it is not done, and `errorSubmissions` is what the retry tool reads.
 *
 * Returns false for a submission that could not be extracted; the caller decides whether that fails a job.
 * A missing step input THROWS instead, because that is not a fact about this submission — the exam or its
 * task tree is unreadable, every submission would "fail" for it, and marking them all as errored would
 * hand the teacher a retry that cannot possibly work.
 */
export async function extractAnswersForSubmission(examId: string, submissionId: string): Promise<boolean> {
  const input = await loadStepInput(examId);
  if (!input) throw new Error(`Answer extraction has no step input for exam ${examId}`);

  await markUnitStarted(examId, 'answer', submissionId);

  const cost = createCostTracker(examId);
  try {
    const ok = await processSubmissionById(
      submissionId,
      input.minimalTasksJson,
      c => cost.addCost(c, 'studentAnswerExtraction'),
      examId,
      'answer',
    );
    if (ok) await markUnitFinished(examId, 'answer', submissionId);
    else await addErrorSubmissionByExam(examId, submissionId);
    return ok;
  } catch (err) {
    logger.error({ err, submissionId, examId }, 'Processing submission failed');
    await addErrorSubmissionByExam(examId, submissionId);
    return false;
  } finally {
    await cost.flush();
  }
}
